import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api, downloadNudgeStatsCsv } from '../lib/api'
import { Users, Download, Info, BarChart3, TrendingUp, Gem } from 'lucide-react'
import clsx from 'clsx'
import { RoiReport } from '../components/ROIReport'

const INK = '#1a3a40'
const BRAND_TEAL = '#2A7A8A'
const BRAND_SAGE = '#B8C7B1'
const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

interface NudgeStat {
  id: string
  title: string
  category: string
  total_sent: number
  total_opened: number
  total_acted: number
  open_rate: number
  action_rate: number
}

interface BenchmarkData {
  orgRate: number;
  industryRate: number;
  industryName: string;
  isAvailable: boolean;
  peerCount: number;
}

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
]

export function AnalyticsPage() {
  const [stats, setStats] = useState<NudgeStat[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState(30)
  const [benchmark, setBenchmark] = useState<BenchmarkData | null>(null)
  const [roiData, setRoiData] = useState<any>(null)
  const [pulseData, setPulseData] = useState<{
    stats: {
      inspiring: number;
      developing: number;
      planning: number;
      vision: number;
      change: number;
      response_count: number;
    } | null;
    isAnonymized: boolean;
    count?: number;
  } | null>(null)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.getNudgeStats(period),
      api.getPulseStats(period),
      api.getBenchmark(),
      api.getRoi(period)
    ]).then(([nStats, pStats, bData, rData]) => {
      setStats((nStats as { stats: NudgeStat[] }).stats)
      setPulseData(pStats as any)
      setBenchmark(bData as BenchmarkData)
      setRoiData(rData)
    }).finally(() => setLoading(false))
  }, [period])

  const topNudges = stats.slice(0, 10)
  const avgActionRate = stats.length
    ? Math.round(stats.reduce((s, n) => s + n.action_rate, 0) / stats.length)
    : 0
  const totalSent = stats.reduce((s, n) => s + n.total_sent, 0)
  const totalActed = stats.reduce((s, n) => s + n.total_acted, 0)

  const barFill = (i: number) => (i < 3 ? BRAND_TEAL : BRAND_SAGE)

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div
        className="mb-6 rounded-xl border px-4 py-3 text-sm"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER, color: '#3d6068' }}
      >
        <span className="font-medium text-ink">For stakeholders:</span> these charts are aggregate and suitable for
        internal reporting. See{' '}
        <Link to="/security#what-we-measure" className="text-brand-teal font-medium underline underline-offset-2">
          what we measure
        </Link>{' '}
        (Security page) for a user-friendly summary of metrics. For how we frame{' '}
        <strong className="text-ink">dollar ROI</strong> on this page, see{' '}
        <Link
          to="/dashboard/behavioral-roi-methodology"
          className="text-brand-teal font-medium underline underline-offset-2"
        >
          behavioral ROI methodology &amp; research
        </Link>
        .
      </div>

      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Analytics</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>
            Organization-wide performance and ROI (all teammates combined)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors border"
                style={period === opt.value ? {
                  background: '#2A7A8A', color: '#fff', borderColor: '#2A7A8A',
                  boxShadow: '0 0 20px -6px rgba(42,122,138,0.4)'
                } : {
                  background: '#FDFCF8', color: '#6b8a8f', borderColor: '#e0d9cc'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => void downloadNudgeStatsCsv(period)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-warm-border bg-warm-card text-ink hover:bg-warm-inset transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <RoiReport data={roiData} loading={loading} />

        <div 
          className="rounded-2xl border p-6 bg-white shadow-card flex flex-col"
          style={{ borderColor: WARM_BORDER }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-sm font-semibold text-ink">Industry Benchmark</h3>
              <p className="text-[10px] text-ink-soft mt-0.5">Vs. {benchmark?.industryName || 'Sector'} Peers</p>
            </div>
            <BarChart3 size={18} className="text-brand-teal opacity-50" />
          </div>

          {!benchmark?.isAvailable ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <Gem size={32} className="text-warm-border mb-3" />
              <p className="text-sm text-ink-muted font-medium italic">Benchmarking Pending</p>
              <p className="text-[10px] text-ink-faint mt-2 leading-relaxed">
                We need 5+ organizations in the {benchmark?.industryName || 'your'} industry 
                to show anonymized benchmarks. (Currently: {benchmark?.peerCount || 0}/5)
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-center gap-8">
              <div>
                <div className="flex justify-between items-end mb-2">
                  <span className="text-xs font-medium text-ink-soft">Your Team</span>
                  <span className="text-lg font-bold text-brand-teal">{benchmark.orgRate}%</span>
                </div>
                <div className="h-3 w-full bg-warm-inset rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-brand-teal rounded-full" 
                    style={{ width: `${benchmark.orgRate}%` }} 
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-end mb-2">
                  <span className="text-xs font-medium text-ink-soft">{benchmark.industryName} Median</span>
                  <span className="text-lg font-bold text-ink-muted">{benchmark.industryRate}%</span>
                </div>
                <div className="h-3 w-full bg-warm-inset rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-warm-border rounded-full" 
                    style={{ width: `${benchmark.industryRate}%` }} 
                  />
                </div>
              </div>

              <div className="mt-auto pt-4 border-t border-warm-border/40 text-center">
                <p className="text-[11px] font-medium text-brand-teal">
                  {benchmark.orgRate >= benchmark.industryRate 
                    ? "🎉 Top quartile performance" 
                    : `Focus on friction reduction to reach the ${benchmark.industryRate}% benchmark`}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-3">
        {[
          { label: 'Nudges Sent', value: totalSent.toLocaleString() },
          { label: 'Actions Taken', value: totalActed.toLocaleString() },
          { label: 'Avg. Action Rate', value: `${avgActionRate}%` },
        ].map(({ label, value }, i) => (
          <div
            key={label}
            className={clsx(
              'rounded-2xl border p-5 shadow-card relative overflow-hidden',
              i === 0 && 'animate-breathe'
            )}
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div
              className={clsx(
                'absolute inset-0 pointer-events-none opacity-80',
                i % 2 === 0 ? 'glow-radial-teal' : 'glow-radial-sage'
              )}
              aria-hidden
            />
            <div className="relative">
              <p className="text-xs text-ink-soft mb-1">{label}</p>
              <p className="text-2xl font-bold text-ink">{loading ? '—' : value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-8 flex items-start gap-2 text-[11px] text-ink-soft bg-warm-inset/50 p-3 rounded-xl border border-warm-border/40">
        <Info size={12} className="shrink-0 mt-0.5 text-brand-teal" />
        <p className="leading-relaxed">
          <strong>Metric Trend:</strong> Your average action rate is {avgActionRate}% over the last {period} days. 
          {avgActionRate >= 40 
            ? " This indicates high team engagement and low behavioral friction." 
            : " Action rates typically fluctuate alongside team workload and nudge relevance."}
          {" "}Rates reflect unique completions within 24h of delivery.
        </p>
      </div>

      <div
        className="rounded-2xl border p-6 mb-6 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <h2 className="text-sm font-semibold text-ink mb-4">Top Nudges by Action Rate</h2>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">Loading…</div>
        ) : topNudges.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">
            No data yet — nudges will appear here once delivered.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topNudges} layout="vertical" margin={{ left: 8, right: 8 }}>
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
                tick={{ fontSize: 11, fill: INK }}
                tickLine={false}
                axisLine={{ stroke: WARM_BORDER }}
              />
              <YAxis
                type="category"
                dataKey="title"
                tick={{ fontSize: 11, fill: INK }}
                tickLine={false}
                axisLine={{ stroke: WARM_BORDER }}
                width={160}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  return (
                    <div
                      className="rounded-lg px-3 py-2 text-xs shadow-card border max-w-xs"
                      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
                    >
                      <p className="text-ink font-medium mb-1">{String(label)}</p>
                      <p className="text-brand-teal font-semibold">
                        {(payload[0].value as number)}% action rate
                      </p>
                    </div>
                  )
                }}
              />
              <Bar dataKey="action_rate" radius={[0, 6, 6, 0]}>
                {topNudges.map((_, i) => (
                  <Cell key={i} fill={barFill(i)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Leadership Pulse Section */}
      <div
        className="rounded-2xl border p-6 mb-8 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">Leadership Pulse (rolling window)</h2>
            <p className="text-[11px] text-ink-soft mt-0.5">
              Aggregate 1-min Slack surveys · matches the period above. Month-to-date pulse (e.g.{' '}
              <span className="font-medium text-ink">
                Leadership Pulse ({new Date().toLocaleString('en-US', { month: 'long' })})
              </span>
              ) is on Home → Your org activity.
            </p>
          </div>
          {pulseData?.stats && (
            <span className="text-[10px] bg-brand-teal/5 text-brand-teal px-2 py-0.5 rounded-md border border-brand-teal/10 font-medium">
              {pulseData.stats.response_count} responses
            </span>
          )}
        </div>

        {loading ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">Loading…</div>
        ) : pulseData?.isAnonymized ? (
          <div className="h-48 flex flex-col items-center justify-center text-center px-4">
            <Users size={28} className="text-ink-faint mb-3" />
            <p className="text-sm text-ink-muted font-medium">Anonymity Guard Active</p>
            <p className="text-xs text-ink-soft mt-1">Pulse results are hidden until at least 3 team members have responded. (Current: {pulseData.count ?? 0})</p>
          </div>
        ) : pulseData?.stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { name: 'Inspiring', score: pulseData.stats.inspiring },
                  { name: 'Developing', score: pulseData.stats.developing },
                  { name: 'Planning', score: pulseData.stats.planning },
                  { name: 'Vision', score: pulseData.stats.vision },
                  { name: 'Change', score: pulseData.stats.change },
                ]}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: INK }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: INK }} axisLine={false} tickLine={false} width={25} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null
                    return (
                      <div className="rounded-lg px-3 py-2 text-xs shadow-card border bg-white border-warm-border">
                        <p className="font-semibold text-brand-teal">{payload[0].value} / 5.0</p>
                      </div>
                    )
                  }}
                />
                <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                  { [0,1,2,3,4].map((_, i) => <Cell key={i} fill={i === 0 ? BRAND_TEAL : BRAND_SAGE} />) }
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="space-y-3">
              <PulseDimension label="Inspiring Others" score={pulseData.stats.inspiring} />
              <PulseDimension label="Developing Talent" score={pulseData.stats.developing} />
              <PulseDimension label="Effective Planning" score={pulseData.stats.planning} />
              <PulseDimension label="Vision Communication" score={pulseData.stats.vision} />
              <PulseDimension label="Change Management" score={pulseData.stats.change} />
            </div>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">
            No pulse data yet for this period.
          </div>
        )}
      </div>

      <div
        className="rounded-2xl border overflow-hidden shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: WARM_BORDER }}>
          <h2 className="text-sm font-semibold text-ink">All Nudges</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: WARM_BORDER }}>
                {['Nudge', 'Category', 'Sent', 'Opened', 'Acted', 'Action Rate'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-medium text-ink-soft">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-warm-border/60">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-3 bg-warm-inset rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
                : stats.map(n => (
                  <tr
                    key={n.id}
                    className="border-b border-warm-border/60 hover:bg-warm-inset/50 transition-colors"
                  >
                    <td className="px-5 py-3 text-ink font-medium max-w-[200px] truncate">{n.title}</td>
                    <td className="px-5 py-3 text-ink-muted capitalize">{n.category}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_sent}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_opened}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_acted}</td>
                    <td className="px-5 py-3">
                      <RateBadge value={n.action_rate} />
                    </td>
                  </tr>
                ))
              }
              {!loading && stats.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-ink-muted text-sm">
                    No data for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function RateBadge({ value }: { value: number }) {
  const color =
    value >= 50 ? 'text-emerald-800' : value >= 25 ? 'text-amber-800' : 'text-ink-muted'
  return <span className={clsx('font-medium tabular-nums', color)}>{value ?? 0}%</span>
}

function PulseDimension({ label, score }: { label: string; score: number }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="font-medium text-ink-muted">{label}</span>
        <span className="text-brand-teal font-bold">{score} / 5.0</span>
      </div>
      <div className="h-1.5 w-full bg-warm-inset rounded-full overflow-hidden">
        <div 
          className="h-full bg-brand-teal rounded-full transition-all duration-1000" 
          style={{ width: `${(score / 5) * 100}%` }} 
        />
      </div>
    </div>
  )
}
